import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import {
  listPayouts,
  requestPayout,
  getWalletSummary,
  getPayoutGateways,
  getProfile,
  getPayoutChargeQuote,
} from "../api";

/* Per-gateway commission wallet labels */
const GATEWAY_LABELS = {
  general: "General",
  paytm: "Paytm",
  cashfree: "Cashfree",
  slpe: "SLPE",
  superpe: "SuperPe",
};
const gatewayLabel = (g) =>
  GATEWAY_LABELS[String(g || "").toLowerCase()] || g || "Commission";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export default function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  /* Payout charge (Gmax) for the entered amount — charged on top. */
  const [charge, setCharge] = useState(0);
  const [walletType, setWalletType] = useState("main");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [gateways, setGateways] = useState([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState("");

  /* Beneficiary bank details — where the payout is sent. Prefilled from the
     user's profile when available; required (Gmax rejects an empty account). */
  const [accountHolderName, setAccountHolderName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");

  const fetchPayouts = useCallback(() => {
    setLoading(true);
    listPayouts(page)
      .then(({ data }) => {
        setPayouts(data.payouts || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  const fetchBalance = () => {
    getWalletSummary()
      .then(({ data }) => setSummary(data))
      .catch(() => {});
  };

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  useEffect(() => {
    fetchBalance();
  }, []);

  /* Prefill beneficiary bank details from the user's profile, if saved. */
  useEffect(() => {
    getProfile()
      .then(({ data }) => {
        setAccountHolderName(
          `${data.first_name || ""} ${data.last_name || ""}`.trim(),
        );
        setBankAccountNumber(data.bank_account_number || "");
        setIfscCode(data.ifsc_code || "");
      })
      .catch(() => {});
  }, []);

  /* Withdrawable wallets: Main + each commission wallet with a balance */
  const wallets = [
    {
      type: "main",
      label: "Main",
      balance: Number(summary?.mainBalance || 0),
    },
    ...(summary?.commissionWallets || [])
      .filter((w) => Number(w.balance) > 0)
      .map((w) => ({
        type: w.type,
        label: `${gatewayLabel(w.gateway)} Commission`,
        balance: Number(w.balance || 0),
      })),
  ];

  const selectedWallet =
    wallets.find((w) => w.type === walletType) || wallets[0];
  const availableBalance = Number(selectedWallet?.balance || 0);
  const totalDebit = Number(amount || 0) + Number(charge || 0);

  /* Fetch the payout charge for the entered amount (debounced). */
  useEffect(() => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      setCharge(0);
      return;
    }
    const t = setTimeout(() => {
      getPayoutChargeQuote(amt)
        .then(({ data }) => setCharge(Number(data.charge) || 0))
        .catch(() => setCharge(0));
    }, 300);
    return () => clearTimeout(t);
  }, [amount]);
  const isMain = walletType === "main";

  const handleOpenDialog = async () => {
    setOpen(true);
    setGatewaysLoading(true);
    try {
      const { data } = await getPayoutGateways();
      setGateways(data.gateways || []);
      if (data.gateways?.length === 1) setSelectedGateway(data.gateways[0].id);
    } catch {
      setGateways([]);
    }
    setGatewaysLoading(false);
  };

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) return;
    if (totalDebit > availableBalance) {
      setFeedback({
        severity: "error",
        msg: `Amount + charge (₹${totalDebit.toLocaleString("en-IN")}) exceeds available balance (₹${availableBalance.toLocaleString("en-IN")}).`,
      });
      return;
    }
    /* Bank details are required — the payout gateway rejects an empty account. */
    if (!bankAccountNumber.trim() || !ifscCode.trim() || !accountHolderName.trim()) {
      setFeedback({
        severity: "error",
        msg: "Enter the account holder name, account number and IFSC to withdraw.",
      });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const { data } = await requestPayout({
        amount: Number(amount),
        walletType,
        gatewayId: selectedGateway || undefined,
        accountHolderName: accountHolderName.trim(),
        bankAccountNumber: bankAccountNumber.trim(),
        ifscCode: ifscCode.trim().toUpperCase(),
      });
      setFeedback({
        severity: "success",
        msg: isMain
          ? `Instant withdrawal ${data.message || "submitted"}${data.gmaxTxnId ? ` — ref ${data.gmaxTxnId}` : ""}`
          : "Withdrawal request submitted — pending admin approval.",
      });
      setOpen(false);
      setAmount("");
      setSelectedGateway("");
      fetchPayouts();
      fetchBalance();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Withdrawal failed",
      });
    }
    setSubmitting(false);
  };

  const statusColor = (s) => {
    if (s === "completed") return "success";
    if (s === "rejected" || s === "failed") return "error";
    if (s === "processing" || s === "approved") return "info";
    return "warning";
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5">Withdrawals</Typography>
          <Typography variant="body2" color="text.secondary">
            {summary
              ? `Main: ₹${Number(summary.mainBalance || 0).toLocaleString("en-IN")}  •  Commission: ₹${Number(summary.totalCommission || 0).toLocaleString("en-IN")}`
              : "Loading balances..."}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AccountBalanceRoundedIcon />}
          onClick={handleOpenDialog}
          sx={{ borderRadius: 3 }}
        >
          Withdraw
        </Button>
      </Stack>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      <Card sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : payouts.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <AccountBalanceRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">No withdrawals yet</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Wallet</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontWeight: 700 }}
                    >
                      {p.order_no || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        p.wallet_type === "main"
                          ? "Main"
                          : p.wallet_type?.startsWith("commission_")
                            ? `${gatewayLabel(p.wallet_type.replace("commission_", ""))} Commission`
                            : "Commission"
                      }
                      color={p.wallet_type === "main" ? "primary" : "warning"}
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    ₹{Number(p.amount || 0).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.status}
                      color={statusColor(p.status)}
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: 12 }}
                    >
                      {p.bank_account_number
                        ? `••••${p.bank_account_number.slice(-4)}`
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                    {formatDate(p.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </Box>
        )}
        {Math.ceil(total / 20) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={Math.ceil(total / 20)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Withdraw dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Withdraw Funds</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Box>
              <FormControl fullWidth>
                <InputLabel>Withdraw from</InputLabel>
                <Select
                  value={selectedWallet ? walletType : ""}
                  label="Withdraw from"
                  onChange={(e) => setWalletType(e.target.value)}
                >
                  {wallets.map((w) => (
                    <MenuItem key={w.type} value={w.type}>
                      {w.label} (₹{w.balance.toLocaleString("en-IN")})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                mt={0.5}
                display="block"
              >
                {isMain
                  ? "Main wallet: instant withdrawal, no approval needed."
                  : "Commission wallet: requires admin approval."}
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Amount (₹)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              helperText={`Available: ₹${availableBalance.toLocaleString("en-IN")}`}
              inputProps={{ min: 1 }}
            />
            <TextField
              fullWidth
              label="Account Holder Name"
              value={accountHolderName}
              onChange={(e) => setAccountHolderName(e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Bank Account Number"
              value={bankAccountNumber}
              onChange={(e) =>
                setBankAccountNumber(e.target.value.replace(/\s/g, ""))
              }
              required
            />
            <TextField
              fullWidth
              label="IFSC Code"
              value={ifscCode}
              onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
              inputProps={{ style: { textTransform: "uppercase" } }}
              required
            />
            {/* Charge breakdown — charge is added on top of the amount. */}
            {Number(amount) > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  fontSize: 14,
                }}
              >
                <Stack direction="row" justifyContent="space-between">
                  <span>Withdrawal amount</span>
                  <span>₹{Number(amount).toLocaleString("en-IN")}</span>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <span>Payout charge (incl. GST)</span>
                  <span>₹{charge.toLocaleString("en-IN")}</span>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ fontWeight: 700, mt: 0.5 }}
                >
                  <span>Total debited</span>
                  <span>₹{totalDebit.toLocaleString("en-IN")}</span>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  You'll receive ₹{Number(amount).toLocaleString("en-IN")} in
                  your bank; the charge is deducted from your wallet.
                </Typography>
              </Box>
            )}
            {gatewaysLoading ? (
              <Skeleton variant="rounded" height={56} />
            ) : gateways.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel>Payment Gateway</InputLabel>
                <Select
                  value={selectedGateway}
                  label="Payment Gateway"
                  onChange={(e) => setSelectedGateway(e.target.value)}
                >
                  {gateways.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !amount || Number(amount) <= 0}
          >
            {submitting
              ? "Submitting..."
              : isMain
                ? "Withdraw (Instant)"
                : "Request Withdrawal"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
